import DataTableBase from "./DataTableBase";
import 'rc-pagination/assets/index.css';
import PlayerSummary from "../player_profile/PlayerSummary";
import React, {useState} from "react";
import {gql, useQuery} from "@apollo/client";
import Tools from "../../../Tools";
import {connect} from "react-redux";
import {toggleSelectPlayer, setPagination, setExpandRow, setSort} from "../../../store/actions/Dashboard/TalentSearch/TalentSearchActions";
import {Image} from "react-bootstrap";
import dustinWilliamsonAvatar from "../../../assets/img/demo/dustin-williamson-avatar.png";

import '../../../assets/sass/components/datatable/player-datatable.scss';

class PlayerDataTable extends DataTableBase {
    /**
     * Returns the table columns configurations.
     * @returns {{dataField: string, text: string, headerTitle: string, sort :boolean}[]} .
     * dataField: the dataField.
     * text: The column display name.
     * sort: Determine if column is sortable.
     * headerTitle:
     */
    columnsConfig = () => {
        return [
            {
                dataField: 'name',
                text: 'Player Name',
                sort: true,
                renderer: (player) => this.renderPlayer(player),
            }, {
                dataField: 'team',
                text: 'Team',
                sort: true,
            }, {
                dataField: 'position',
                text: 'Position',
                sort: true,
            }, {
                dataField: 'age',
                text: 'Age',
                sort: true,
            }, {
                dataField: 'marketValue',
                text: 'Market Value',
                sort: true,
            }, {
                dataField: 'matchesPlayed',
                text: 'Matches Played',
                sort: true,
            }
            , {
                dataField: 'minutesPlayed',
                text: 'Minutes Played',
            }, {
                dataField: 'birthCountry',
                text: 'Birth Country',
            }, {
                dataField: 'passportCountry',
                text: 'Passport Country',
            }, {
                dataField: 'foot',
                text: 'Foot',
            }, {
                dataField: 'height',
                text: 'Height',
            }, {
                dataField: 'weight',
                text: 'Weight',
            }, {
                dataField: 'onLoan',
                text: 'On Loan',
            },
        ];
    };

    renderPlayer = (player) => {
        return (
            <div className="player-avatar-name">
                <Image roundedCircle src={player.avatar}/>
                <span> {player.name} </span>
            </div>
        );
    };

    renderExpandedRow = (row, rowIndex) => {
        const {page, onExpanding} = this.props;

        return (
            <div className="expanded-table-row">
                <PlayerSummary id={row.id} page={page} onExpanding={(loading, error, done) => this.handleExpanding(row.id, loading, error, done)}/>
            </div>
        );
    };

    getSelectedRows = () => {
        const {selectedRows} = this.props;
        return selectedRows;
    };

    /**
     * Handle Expanding rows.
     * @param id The row id
     * @param loading
     * @param error
     * @param done
     */
    handleExpanding = (id, loading, error, done) => {
        const {onExpanding} = this.props;

        if (typeof onExpanding === 'function') {
            onExpanding(loading, error, done);
        }
    };

    /**
     * Extra classes for datatable.
     * @type {{table: string}} The extra classes.
     */
    extraClasses = {
        table: 'player-datatable',
    };
}


const PlayerDataTableDataWrapper = props => {
    // GraphQL query.
    const query = gql`
        query getPlayers (
            $q: String,
            $regions: [String!],
            $countries: [String!],
            $seasons: [String!],
            $competitions: [String!],
            $performance: PerformanceArgs,
            $skip: Int,
            $take: Int,
            $order: [String!],
        ) {
            players: players(
                q: $q,
                region:  $regions,
                nationality: $countries,
                passport: $countries,
                season: $seasons,
                competition: $competitions,
                performance: $performance,
                skip: $skip,
                take: $take,
                order: $order
            ){
                total
                filteredFrom
                results {
                    ...playerFields
                }
            }
        }

        fragment playerFields on PlayerModel{
            id
            avatar
            name
            position
            dob
            marketValue
            onLoan
            height
            weight
            foot
            overallScore
            wage
            team {
                name
            }
            careerStats{
                matchesPlayed
                minutesPlayed
            }
            nationality{
                name
            }
            passport{
                name
            }
        }
    `;

    // Reformat response.
    const formatter = (players) => {
        if (!players)
            return [];

        return players.map(player => ({
            id: player.id,
            name: player.name,
            avatar: player?.avatar?.length ? player?.avatar : dustinWilliamsonAvatar,
            team: player.team.name,
            position: player.position,
            age: Tools.calculateAge(new Date(player.dob)),
            marketValue: Tools.formatterNumber(player.marketValue),
            matchesPlayed: player.careerStats[0]?.matchesPlayed,
            minutesPlayed: player.careerStats[0]?.minutesPlayed,
            birthCountry: player.nationality?.name,
            passportCountry: player.passport?.name,
            foot: Tools.capitalize(player.foot),
            height: player.height,
            weight: player.weight,
            onLoan: Tools.capitalize(Tools.yesNo(player.onLoan))
        }));
    };

    // Get filters values from props.
    const {search, tempSearch, regions, countries, seasons, competitions, performance: _performance} = props;
    const [performance, setPerformance] = useState(_performance);
    const [expanding, setExpanding] = useState(false);

    Tools.delay(() => setPerformance(_performance), 500, 'setPerformance');

    const {currentPage, pageSize} = props;
    const {sortField, sortOrder} = props;
    console.log(sortField, sortOrder);

    const variables = {
        q: Tools.convertEmptyToNull(search),
        regions: Tools.convertEmptyToNull(regions.map(region => region.value)),
        countries: Tools.convertEmptyToNull(countries.map(country => country.value)),
        seasons: Tools.convertEmptyToNull(seasons.map(season => season.value)),
        competitions: Tools.convertEmptyToNull(competitions.map(competition => competition.value)),
        performance: performance,
        skip: (currentPage - 1) * pageSize,
        take: pageSize,
        order: sortField && [sortField, sortOrder]
    };

    // Fetch data.
    const {loading: queryLoading, error, data} = useQuery(query, {
        variables: variables,
        notifyOnNetworkStatusChange: true,
    });

    // Update loading.
    const loading = queryLoading | expanding | search !== tempSearch | performance !== _performance;

    // Format data.
    const rows = formatter(data?.players?.results);
    const filteredFrom = data?.players?.filteredFrom;
    const total = data?.players?.total;

    // Pass props.
    const feedbackProps = {
        loading: loading,
        error: error,
    };

    const {page} = props;
    const paginationProps = {
        page: page,
        currentPage: currentPage,
        pageSize: pageSize,
        total: total,
        filteredFrom: filteredFrom,
    };

    const {selectedPlayers, expandedRows} = props;
    const dataProps = {
        rows: rows,
        selectedRows: selectedPlayers,
        expandedRows: expandedRows,
    };

    const {setPagination, toggleSelectPlayer, setExpandRow, setSort} = props;
    const handlersProp = {
        // onExpanding: (loading, error, done) => setExpanding(loading),
        onPageChange: (pagination) => setPagination(pagination),
        onRowSelect: (row) => toggleSelectPlayer(row),
        onExpandRow: (row, isExpand) => setExpandRow(row, isExpand),
        onSort: (field, order) => setSort(field, order),
    };

    return (
        <PlayerDataTable
            {...feedbackProps}
            {...paginationProps}
            {...dataProps}
            {...handlersProp}

            enableSelect={selectedPlayers.length < 3}
        />
    );
};

const mapStateToProps = state => {
    return {
        performance: state.talentSearch.filters.performance,
        regions: state.talentSearch.filters.regions,
        countries: state.talentSearch.filters.countries,
        seasons: state.talentSearch.filters.seasons,
        competitions: state.talentSearch.filters.competitions,

        selectedPlayers: state.talentSearch.datatable.selectedPlayers,
        expandedRows: state.talentSearch.datatable.expandedRows,

        currentPage: state.talentSearch.datatable.currentPage,
        pageSize: state.talentSearch.datatable.pageSize,
        search: state.talentSearch.search,
        tempSearch: state.talentSearch.tempSearch,

        sortField: state.talentSearch.datatable.sortField,
        sortOrder: state.talentSearch.datatable.sortOrder,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        toggleSelectPlayer: (player) => dispatch(toggleSelectPlayer(player)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setExpandRow: (row, isExpand) => dispatch(setExpandRow(row, isExpand)),
        setSort: (field, order) => dispatch(setSort(field, order)),
    };
};
export default connect(mapStateToProps, mapDispatchToProps)(PlayerDataTableDataWrapper);
